import React from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { niceNumber, niceDateTime } from "@/lib/util";
import local_settings, { FIELD_LAST_PLANT } from "@/lib/localSettings";
import { ReduxFilters } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import { UPDATE_STOCK_REQUIREMENTS_HEIGHT } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import { UPDATE_STOCK_REQUIREMENTS } from "@/modules/pg/shared/planningTable/reducers/planningTable/stockRequirements";
import { LinesOrdersLogic } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import * as t2 from "@/lib/simulationReportModels";
import StockRequirementsFilters from "./StockRequirementsFilters";
import { numStockRequirementsAfterResolution } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import { UserActions } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import Loader from "@/modules/shared/ui/Loader";

import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { StockRequirement } from "@/modules/pg/shared/planningTable/reducers/planningTable/stockRequirements";
import type { LineOrders } from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import type { StockRequirementActions } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";

type ActionTypes = StockRequirementActions;

type OwnProps = {
    report?: t2.IReportModelEx | null;
    current_report_uuid?: string;
    show_planning_details_filter: boolean;
    line_group_uuid: string;
    original_report_uuid?: string | null;
    lines_orders?: LineOrders[];
};

type Props = OwnProps & ReduxProps;

type State = { loading: boolean };

class StockRequirementsList extends React.Component<Props, State> {
    state: State = {
        loading: false,
    };

    _loadGeneration = 0;

    componentDidMount() {
        if (this.props.lines_orders?.length > 0) {
            this.load();
        }
    }

    componentWillUnmount() {
        this._loadGeneration++;
    }

    componentDidUpdate(prev_props: Props) {
        if (prev_props.line_group_uuid !== this.props.line_group_uuid) {
            this.setState({ loading: true });
        }

        if (prev_props.lines_orders !== this.props.lines_orders && this.props.lines_orders?.length > 0) {
            this.load();
        }

        if (
            prev_props.selected_stock_requirement !== this.props.selected_stock_requirement &&
            this.props.selected_stock_requirement
        ) {
            const uuid = this.props.selected_stock_requirement;
            const rect = document.querySelector(`[name='stock-requirement-row'][data-uuid='${uuid}']`);

            if (rect) {
                rect.scrollIntoView({ block: "center", inline: "center" });
            }
        }
    }

    async load() {
        this.setState({ loading: true });

        const loadGeneration = ++this._loadGeneration;
        const plant_uuid = local_settings.getItem(FIELD_LAST_PLANT);
        const lines_orders = this.props.lines_orders;

        if (!lines_orders || !plant_uuid || lines_orders.length === 0) {
            console.log("No report...", lines_orders);
            console.log("No plant...", plant_uuid);
            this.setState({ loading: false });
            return;
        }

        const line_uuids = lines_orders.map(lol => lol.line_uuid);

        let stock_requirements = await LinesOrdersLogic.getStockRequirements(
            plant_uuid,
            line_uuids,
            this.props.properties
        );

        if (loadGeneration !== this._loadGeneration) {
            return;
        }

        stock_requirements = stock_requirements.map(sr => ({
            date_required: sr.date_required,
            date_expected: sr.date_expected,
            shift_start_time: sr.shift_start_time,
            quantity: sr.quantity,
            material_title: sr.material_title,
            material_external_id: sr.material_external_id,
            material_uuid: sr.material_uuid,
            uuid: sr.uuid,
            shift_uuid: sr.shift_uuid,
            plant_uuid,
            transaction_external_id: sr.transaction_external_id,
        }));

        this.props.reduxDispatch({
            type: UPDATE_STOCK_REQUIREMENTS,
            data: stock_requirements,
        });

        this.props.reduxDispatch({
            type: UPDATE_STOCK_REQUIREMENTS_HEIGHT,
            data: {
                num_stock_requirements: numStockRequirementsAfterResolution(stock_requirements, this.props.properties),
            },
        });

        this.setState({ loading: false });
    }

    onClick = (stock_requirement: StockRequirement) => async () => {
        this.props.reduxDispatch({ type: "report_loading", data: true });

        UserActions.onStockRequirementClick(
            stock_requirement.uuid,
            stock_requirement.material_external_id,
            this.props.lines_orders ?? [],
            this.props.reduxDispatch,
            this.props.selected_stock_requirement === stock_requirement.uuid
        );
    };

    renderStockRequirement = (stock_requirement: StockRequirement) => {
        const is_selected = this.props.selected_stock_requirement === stock_requirement.uuid;
        const selected_border = is_selected ? "1px solid black" : undefined;
        const insights_map_stock_requirements = this.props.insights_map_stock_requirements;

        let backgroundColor = "";
        if (insights_map_stock_requirements && insights_map_stock_requirements.has(stock_requirement.uuid)) {
            backgroundColor = "rgb(254, 243, 242)";
        }

        return (
            <React.Fragment key={stock_requirement.uuid}>
                <tr
                    data-uuid={stock_requirement.uuid}
                    style={{
                        cursor: "pointer",
                        backgroundColor,
                        borderTop: selected_border,
                        borderLeft: selected_border,
                        borderRight: selected_border,
                    }}
                    onClick={this.onClick(stock_requirement)}
                    // @ts-ignore
                    name="stock-requirement-row"
                >
                    <td>
                        <strong>{stock_requirement.material_external_id}</strong> <br />
                        {stock_requirement.material_title}
                    </td>
                    <td>{niceDateTime(new Date(stock_requirement.shift_start_time))}</td>
                    <td>{niceNumber(Math.abs(stock_requirement.quantity), 10)}</td>
                </tr>
                <tr
                    onClick={this.onClick(stock_requirement)}
                    style={{
                        marginBottom: "5px",
                        backgroundColor,
                        borderBottom: selected_border,
                        borderLeft: selected_border,
                        borderRight: selected_border,
                    }}
                >
                    <td colSpan={3}>{stock_requirement.transaction_external_id}</td>
                </tr>
                <tr>
                    <td style={{ height: "20px" }}></td>
                </tr>
            </React.Fragment>
        );
    };

    getStockRequirements = (): StockRequirement[] => {
        return ReduxFilters.filterStockRequirements(
            this.props.lines_orders,
            this.props.unscheduled_orders,
            this.props.stock_requirements,
            this.props.orders_filters,
            this.props.stock_requirements_filters,
            this.props.insights_map_stock_requirements ?? new Map()
        );
    };

    renderTable() {
        if (this.state.loading) {
            return <Loader small />;
        }
        const stock_requirements = this.getStockRequirements();

        let el: React.ReactNode | React.ReactElement[] = stock_requirements.map(this.renderStockRequirement);
        if (stock_requirements.length === 0) {
            el = (
                <tr>
                    <td colSpan={3} style={{ padding: "15px" }}>
                        <span>{translate("common.no_data", "No data available")}</span>
                    </td>
                </tr>
            );
        }

        return (
            <div className="planning-table__row mt-0" style={{ caretColor: "transparent" }}>
                <table className="planning-table__sortable-list">
                    <thead>
                        <tr>
                            <th className="sortable-element__material">{translate("common.material", "Material")}</th>
                            <th className="sortable-element__workorder">{translate("common.date", "Date")}</th>
                            <th className="sortable-element__unit">{translate("common.quantity", "Quantity")}</th>
                        </tr>
                    </thead>
                    <tbody>{el}</tbody>
                </table>
            </div>
        );
    }

    render() {
        return (
            <div className="planning-details-unscheduled-orders d-flex flex-column flex-grow-1 overflow-auto">
                <div className="p-3">
                    <StockRequirementsFilters />
                </div>
                <div className="unscheduled-orders-mode flex-grow-1 overflow-auto">
                    <div className="unscheduled-orders-container">{this.renderTable()}</div>
                </div>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const stock_requirements = state.gantt_chart_stock_requirements.stock_requirements;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;
    const stock_requirements_filters = state.gantt_chart_stock_requirements_filter;
    const insights_map_stock_requirements = state.gantt_chart_insights.insights_map_stock_requirements;

    return {
        properties: state.gantt_chart_properties,
        stock_requirements: stock_requirements,
        stock_requirements_filters,
        selected_stock_requirement: stock_requirements_filters.selected_stock_requirement,
        lines_orders,
        orders_filters: state.gantt_chart_filters,
        unscheduled_orders,
        report_loading: state.gantt_chart_report.report_loading,
        insights_map_stock_requirements,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(StockRequirementsList);
